import { Sets } from "../../components/workout/types";
import { getUnitCategory } from "../../utils/units";
import {
  ExerciseTypeSet,
  ExerciseTypeExtra,
  UnitType,
  ExerciseSetNumberType,
} from "../../constants";
import { convertUnits } from "../../constants";

export const checkIsPartOfSuperset = (
  exercises: any[],
  currentIndex: number,
): boolean => {
  const current = exercises[currentIndex];
  const prev = exercises[currentIndex - 1];
  const next = exercises[currentIndex + 1];

  if (!current?.superset) return false;

  const matchesNext = next && next.superset === current.superset;
  const matchesPrev = prev && prev.superset === current.superset;

  return !!(matchesNext || matchesPrev);
};
export const isEmptyPlanValue = (val: unknown): boolean => {
  const s = String(val ?? "").trim();
  return s === "" || s === "-";
};

export const parseNumber = (val: unknown): number => {
  if (val === null || val === undefined) return 0;

  const cleaned = String(val)
    .split("-")[0]
    .replace(/[^0-9.]/g, "");

  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

export const parseRange = (val: unknown): { min: number; max: number } => {
  if (!val) return { min: 0, max: 0 };

  const parts = String(val).split(/[-/ ]+/);

  if (parts.length >= 2) {
    return {
      min: parseNumber(parts[0]),
      max: parseNumber(parts[1]),
    };
  }

  const single = parseNumber(val);
  return { min: single, max: single };
};

type DiffResult = {
  repsDiff: number;
  weightDiff: number;
};

export const calculateSetDiff = (
  actualSet: Sets,
  plannedSet?: Sets | null,
): DiffResult => {
  if (!actualSet.checked || !plannedSet) {
    return { repsDiff: 0, weightDiff: 0 };
  }

  const actualReps = parseNumber(actualSet.reps);
  const plannedReps = parseRange(plannedSet.reps);

  const actualWeight = parseNumber(actualSet.weight);
  const plannedWeightRaw = plannedSet?.weight ?? "";
  const weightDiff = isEmptyPlanValue(plannedWeightRaw)
    ? 0
    : diffByRange(actualWeight, parseRange(plannedWeightRaw));

  return {
    repsDiff: diffByRange(actualReps, plannedReps),
    weightDiff,
  };
};

const diffByRange = (
  actual: number,
  planned: { min: number; max: number },
): number => {
  if (actual < planned.min) return actual - planned.min;
  if (actual > planned.max) return actual - planned.max;
  return 0;
};
export type NormalizedRange = {
  min: number;
  max: number;
};

export type NormalizedValue = {
  value: number | string | null;
  planned: NormalizedRange | null;
  diff: number | null;
};

export type NormalizedSet = {
  reps: NormalizedValue;
  weight: NormalizedValue & {
    unit: string;
    isCompatible: boolean;
    originalWeight?: string | number;
    originalUnits?: string;
  };
  extra?: NormalizedValue & {
    type: ExerciseTypeExtra;
    isCompatible: boolean;
    originalExtra?: string | number;
    originalExtraType?: string;
  };
  checked?: boolean;
  type?: ExerciseSetNumberType;
};

const normalizeReps = (
  completedSet: Sets,
  plannedSet?: Sets | null,
): NormalizedValue => {
  const displayRaw = completedSet.checked
    ? completedSet.completedReps || completedSet.reps
    : completedSet.reps || plannedSet?.reps;
  const numericValue = parseNumber(
    completedSet.completedReps || completedSet.reps,
  );

  const planned = plannedSet?.reps ? parseRange(plannedSet.reps) : null;

  return {
    value: displayRaw,
    planned,
    diff:
      completedSet.checked && planned
        ? diffByRange(numericValue, planned)
        : null,
  };
};

const normalizeWeight = ({
  completedSet,
  plannedSet,
  userUnits,
  originalUnits,
  exerciseTypeSet,
}: {
  completedSet: Sets;
  plannedSet?: any | null;
  userUnits: string;
  originalUnits?: string;
  exerciseTypeSet: ExerciseTypeSet;
}): NormalizedSet["weight"] => {
  const currentCategory = getUnitCategory(userUnits, exerciseTypeSet);
  const originalCategory = originalUnits
    ? getUnitCategory(originalUnits, exerciseTypeSet)
    : null;

  const isCompatible =
    !!currentCategory &&
    !!originalCategory &&
    currentCategory === originalCategory;

  let rawPlannedWeight = plannedSet?.weight ?? plannedSet?.value ?? "";
  if (isEmptyPlanValue(rawPlannedWeight)) rawPlannedWeight = "";
  const value = parseNumber(completedSet.weight);
  const plannedRange = rawPlannedWeight ? parseRange(rawPlannedWeight) : null;

  let finalRange = plannedRange;
  let weightDiff: number | null = null;

  if (isCompatible && finalRange && completedSet.checked) {
    let finalPlannedMin = finalRange.min;
    let finalPlannedMax = finalRange.max;

    if (originalUnits && originalUnits !== userUnits) {
      const unitTypeMap: Record<string, any> = {
        [ExerciseTypeSet.WEIGHT]: UnitType.WEIGHT,
        [ExerciseTypeSet.DISTANCE]: UnitType.DISTANCE,
        [ExerciseTypeSet.TIME]: UnitType.TIME,
      };

      const type = unitTypeMap[exerciseTypeSet];

      if (type) {
        finalPlannedMin = convertUnits(
          type,
          originalUnits,
          userUnits,
          finalRange.min,
        );
        finalPlannedMax = convertUnits(
          type,
          originalUnits,
          userUnits,
          finalRange.max,
        );

        finalRange = { min: finalPlannedMin, max: finalPlannedMax };
      }
    }

    weightDiff =
      Math.round(
        diffByRange(value, { min: finalPlannedMin, max: finalPlannedMax }) * 10,
      ) / 10;
  }

  return {
    value,
    planned: finalRange,
    diff: weightDiff,
    unit: userUnits,
    isCompatible,
    originalWeight: rawPlannedWeight,
    originalUnits: originalUnits,
  };
};

const normalizeExtra = (
  completedSet: Sets,
  plannedSet?: any | null,
  typeExtraMeasurement?: ExerciseTypeExtra,
): NormalizedSet["extra"] => {
  const plannedExtraType =
    plannedSet?.typeExtraMeasurement ||
    plannedSet?.originalContent?.typeExtraMeasurement ||
    plannedSet?.originalExtraType ||
    plannedSet?.extraType ||
    "";

  const normalize = (s: any) =>
    String(s || "")
      .trim()
      .toUpperCase();
  const actT_Type = normalize(typeExtraMeasurement);
  const planT_Type = normalize(plannedExtraType);

  const actVal = parseNumber(completedSet.extraMeasurement);
  const planT_Value = plannedSet?.extraMeasurement ?? "";

  const planRangeFromPlan =
    planT_Value !== "" ? parseRange(String(planT_Value)) : null;

  const hasExtraType = !!(
    typeExtraMeasurement && typeExtraMeasurement !== ExerciseTypeExtra.NONE
  );

  const planRange =
    planRangeFromPlan ??
    (hasExtraType && completedSet.checked ? { min: 0, max: 0 } : null);
  const isCompatible = !!(
    planRange &&
    (actT_Type === planT_Type ||
      actT_Type.includes(planT_Type) ||
      planT_Type.includes(actT_Type))
  );

  return {
    value: actVal,
    planned: planRange as any,
    isCompatible: isCompatible,
    diff:
      completedSet.checked && isCompatible && planRange
        ? diffByRange(actVal, planRange)
        : null,
    type:
      (typeExtraMeasurement as ExerciseTypeExtra) ||
      (plannedExtraType as ExerciseTypeExtra),
    originalExtra: planT_Value,
    originalExtraType: plannedExtraType,
  };
};

export const buildNormalizedSet = ({
  completedSet,
  plannedSet,
  userUnits,
  originalUnits,
  typeExtraMeasurement,
  exerciseTypeSet,
}: {
  completedSet: Sets;
  plannedSet?: any | null;
  userUnits: string;
  originalUnits?: string;
  typeExtraMeasurement?: ExerciseTypeExtra;
  exerciseTypeSet: ExerciseTypeSet;
}): NormalizedSet => {
  const repsData = normalizeReps(completedSet, plannedSet);
  if (completedSet.checked && completedSet.completedReps) {
    repsData.value = completedSet.completedReps;
  }

  return {
    reps: repsData,
    weight: normalizeWeight({
      completedSet,
      plannedSet,
      userUnits,
      originalUnits,
      exerciseTypeSet,
    }),
    extra: normalizeExtra(completedSet, plannedSet, typeExtraMeasurement),
    checked: completedSet.checked,
    type: completedSet.type,
  };
};
